/**
 * WorkspaceEventBus — Typed event emitter for workspace component communication.
 *
 * Every user action in the workspace (code edits, tool changes, setting changes,
 * work offset picks) emits an event through this bus. The context accumulator
 * subscribes and collects these events so the AI agent sees everything the user
 * did between messages.
 */

export type WorkspaceEvent =
  | { type: 'code_edit'; operationId: string; summary: string; linesChanged: number }
  | { type: 'material_change'; oldMaterial: string; newMaterial: string }
  | { type: 'tool_added'; toolNumber: number; toolType: string; diameter: number }
  | { type: 'tool_removed'; toolNumber: number; description: string }
  | { type: 'tool_modified'; toolNumber: number; changes: string }
  | { type: 'work_offset_change'; register: string; x: number; y: number; z: number }
  | { type: 'operation_selected'; operationId: string; label: string }
  | { type: 'operation_added'; operationId: string; label: string }
  | { type: 'operation_removed'; operationId: string; label: string }
  | { type: 'stock_size_change'; oldSize: string; newSize: string }
  | { type: 'units_change'; oldUnits: string; newUnits: string }

export type WorkspaceEventHandler = (event: WorkspaceEvent) => void

export class WorkspaceEventBus {
  private listeners = new Set<WorkspaceEventHandler>()

  on(handler: WorkspaceEventHandler): () => void {
    this.listeners.add(handler)
    return () => this.listeners.delete(handler)
  }

  emit(event: WorkspaceEvent): void {
    for (const handler of this.listeners) {
      handler(event)
    }
  }

  clear(): void {
    this.listeners.clear()
  }
}
